import type { ApiKeyDto } from '@today/types'
import {
	ApiKeyCreateDialogView,
	ApiKeyRenameDialogView,
	ApiKeyRevealDialogView,
	ApiKeyRevokeDialogView,
	type ApiKeyRowView,
	ApiKeysSectionView,
} from '@today/ui-domain'
import type { ApiKeyCapabilities } from '@/auth/api-key-capabilities'
import { useApiKeys } from '@/hooks/useApiKeys'

const dateFormat = new Intl.DateTimeFormat(undefined, { dateStyle: 'medium' })
const formatDate = (iso: string) => dateFormat.format(new Date(iso))

const rowOf = (key: ApiKeyDto): ApiKeyRowView => ({
	apiKeyId: key.apiKeyId,
	name: key.name,
	keyPrefix: key.keyPrefix,
	status: key.status,
	createdAt: formatDate(key.createdAt),
	expiresAt: formatDate(key.expiresAt),
	lastUsedAt: key.lastUsedAt === null ? null : formatDate(key.lastUsedAt),
	revocable: key.revokedAt === null,
})

/** The create, reveal-once, rename and revoke dialogs of the section. */
function ApiKeyDialogs({ apiKeys }: { apiKeys: ReturnType<typeof useApiKeys> }) {
	const { create, rename, revoke, reveal } = apiKeys
	return (
		<>
			<ApiKeyCreateDialogView
				isOpen={create.isOpen}
				name={create.name}
				days={create.days}
				nameError={create.nameError}
				daysError={create.daysError}
				error={create.error}
				isBusy={create.isBusy}
				onNameChange={create.setName}
				onDaysChange={create.setDays}
				onSubmit={create.submit}
				onCancel={create.cancel}
			/>
			<ApiKeyRevealDialogView
				plaintextKey={reveal.plaintextKey}
				copied={reveal.copied}
				onCopy={reveal.copy}
				onClose={reveal.close}
			/>
			<ApiKeyRenameDialogView
				isOpen={rename.target !== null}
				name={rename.name}
				nameError={rename.nameError}
				error={rename.error}
				isBusy={rename.isBusy}
				onNameChange={rename.setName}
				onSubmit={rename.submit}
				onCancel={rename.cancel}
			/>
			<ApiKeyRevokeDialogView
				keyName={revoke.target?.name ?? null}
				error={revoke.error}
				isBusy={revoke.isBusy}
				onConfirm={revoke.confirm}
				onCancel={revoke.cancel}
			/>
		</>
	)
}

/**
 * The Settings "API keys" section (TO-516 REQ-015). Mount it only for a viewer
 * holding `api_key.read`; Create/Rename need `api_key.create`, Revoke needs
 * `api_key.revoke`.
 */
export function ApiKeysSettings({ capabilities }: { capabilities: ApiKeyCapabilities }) {
	const apiKeys = useApiKeys(capabilities.read)
	const { create, rename, revoke } = apiKeys
	const byId = (apiKeyId: string) => apiKeys.keys.find((key) => key.apiKeyId === apiKeyId)

	return (
		<>
			<ApiKeysSectionView
				keys={apiKeys.keys.map(rowOf)}
				isLoading={apiKeys.isLoading}
				loadFailed={apiKeys.loadFailed}
				canCreate={capabilities.create}
				canRevoke={capabilities.revoke}
				onRetry={apiKeys.reload}
				onCreate={create.open}
				onRename={(apiKeyId) => {
					const key = byId(apiKeyId)
					if (key) rename.open(key)
				}}
				onRevoke={(apiKeyId) => {
					const key = byId(apiKeyId)
					if (key) revoke.open(key)
				}}
			/>
			<ApiKeyDialogs apiKeys={apiKeys} />
		</>
	)
}
